import { useState } from "react";
import Typography from "@shared/v2/Typography";
import Button from "@shared/v2/Button";
import TextButton from "@shared/v2/TextButton";
import Modal from "@shared/v2/Modal";
import dayjs from "@shared/dayjs";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import useActiveProducts from "../Products/hooks/useActiveProducts";
import useProducts from "../Products/hooks/useProducts";
import { ProductFamilyIds } from "../Products/constants";
import ProductsStep1 from "../Products/Step1";
import Loading from "../Goals/components/Loader";
import PhoneSystemCancelModal from "../PhoneSystem/TrackingNumbers/Settings/PhoneSystemCancelModal/PhoneSystemCancelModal";
import PhoneSystemChangeModal from "../PhoneSystem/TrackingNumbers/Settings/PhoneSystemChangeModal/PhoneSystemChangeModal";
import { getAgentsAndTrackingNumbers } from "../PhoneSystem/TrackingNumbers/Settings/services";
import { useFeatureFlags } from "../reducers/layoutReducer/selectors";
import { TrackingNumber } from "../PhoneSystem/TrackingNumbers/types";

interface PhoneSystemProps {
  portalUrl: string;
}

const PhoneSystem = ({ portalUrl }: PhoneSystemProps) => {
  const featureFlags = useFeatureFlags() as { phoneSystemChangePlan: boolean };
  const { activeProducts, loading, actions } = useActiveProducts(ProductFamilyIds.PhoneSystem);
  const products = useProducts(ProductFamilyIds.PhoneSystem);
  const activeProduct = activeProducts[0];
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [showChangePlanBar, setShowChangePlanBar] = useState(false);
  const [newProduct, setNewProduct] = useState<(typeof products)[0] | null>(null);
  const [trackingNumbers, setTrackingNumbers] = useState<TrackingNumber[]>([]);
  const [hasLoadedTrackingNumbers, setHasLoadedTrackingNumbers] = useState(false);
  const [loadingTrackingNumbers, setLoadingTrackingNumbers] = useState(false);

  const handleCancelPlan = (
    accountProductId: string | number,
    cancelForm: { status: string; details: { churnCode: string; churnCodeFeedback: string } },
  ) => {
    actions
      .cancelProduct(accountProductId, cancelForm)
      .then(() => setIsCancelModalOpen(false))
      .catch(() => setIsCancelModalOpen(false));
  };

  const handleChangePlan = () => {
    setShowChangePlanBar(true);
  };

  const loadTrackingNumbers = async () => {
    if (!hasLoadedTrackingNumbers) {
      try {
        setLoadingTrackingNumbers(true);
        const response = (await getAgentsAndTrackingNumbers()) as { trackingNumbers?: TrackingNumber[] };
        setTrackingNumbers(Array.isArray(response?.trackingNumbers) ? response.trackingNumbers : []);
        setHasLoadedTrackingNumbers(true);
      } catch {
        setTrackingNumbers([]);
        setHasLoadedTrackingNumbers(true);
      } finally {
        setLoadingTrackingNumbers(false);
      }
    }
  };

  const handleChangeSubscription = async (accountProductId: number, newProductId: number) => {
    await caseTransformingAxios.post("/products/change_subscription", {
      accountProductId,
      newProductId,
    });
    window.location.reload();
  };

  const openCancelModal = async () => {
    setIsCancelModalOpen(true);
    await loadTrackingNumbers();
  };

  if (loading) {
    return (
      <div className="tw-flex tw-min-h-[300px] tw-items-center tw-justify-center">
        <Loading />
      </div>
    );
  }

  if (!activeProduct) {
    return (
      <div className="tw-min-h-[300px] tw-flex tw-flex-col tw-gap-[12px] tw-items-center">
        <div className="tw-text-11d tw-font-semibold tw-tracking-[1px] tw-uppercase tw-text-gray-50">
          Introducing: The Brivity Phone System
        </div>
        {/* @ts-expect-error props */}
        <Typography variant="headline1">
          No more missed{" "}
          <span className="tw-bg-[linear-gradient(90deg,#59C4C4,#336E7B)] tw-bg-clip-text tw-text-transparent">
            leads.
          </span>
        </Typography>
        {/* @ts-expect-error props */}
        <Typography className="tw-max-w-[450px] tw-text-center">
          Never lose an inbound lead again with the <b>Brivity Phone System</b>: the only scalable and
          integrated real estate phone system designed to work specifically with your CRM.
        </Typography>
        {/* @ts-expect-error props */}
        <Button size="medium" onClick={() => window.location.assign("/phone_system?signup")}>
          Learn More Now
        </Button>
      </div>
    );
  }

  return (
    <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-[32px]">
      <div className="tw-flex tw-justify-between tw-items-center">
        <div className="tw-flex tw-flex-col tw-gap-[6px] tw-max-w-[580px]">
          {/* @ts-expect-error props */}
          <Typography variant="headline2">Phone System</Typography>
          {/* @ts-expect-error props */}
          <Typography>
            Never lose an inbound lead again with the Brivity Phone System: the only scalable and integrated
            real estate phone system designed to work specifically with your CRM.
          </Typography>
        </div>
        {/* @ts-expect-error props */}
        <Button
          schema="secondary"
          size="medium"
          className="tw-whitespace-nowrap"
          onClick={() => window.open("/phone_system", "_blank")}
        >
          Launch Phone System
        </Button>
      </div>
      <div className="tw-flex tw-gap-[32px]">
        <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-[12px]">
          {/* @ts-expect-error props */}
          <Typography variant="subtitle-m-bold">Plan Information</Typography>
          <div className="tw-bg-white tw-p-[24px] tw-rounded-[8px] tw-border-solid tw-border-[1.5px] tw-border-gray-10 tw-flex tw-flex-col tw-gap-[8px]">
            {/* @ts-expect-error props */}
            <Typography variant="headline3" className="tw-flex tw-justify-between">
              <span>{activeProduct?.product.name}</span>
              <span>${activeProduct?.product.details.unitPrice}</span>
            </Typography>
            {/* @ts-expect-error props */}
            <Typography variant="body-s" className="!tw-text-gray-50">
              {activeProduct?.product.details.trackingNumberMax} Tracking Numbers,{" "}
              {activeProduct?.product.details.minutesPerMonth} min/month, $
              {activeProduct?.product.details.overageMinutesPrice} per/minute over{" "}
              {activeProduct?.product.details.minutesPerMonth}
            </Typography>
          </div>
          <div className="tw-flex tw-justify-end tw-gap-[8px]">
            {featureFlags.phoneSystemChangePlan && (
              // @ts-expect-error props
              <Button schema="secondary" onClick={handleChangePlan}>
                Change Plan
              </Button>
            )}
            <TextButton
              schema="sentence"
              size="small"
              className={`!tw-text-12d !tw-text-gray-50 ${activeProduct?.endedAt ? "" : "tw-underline"}`}
              onClick={openCancelModal}
              disabled={activeProduct?.endedAt !== null}
            >
              {activeProduct?.endedAt
                ? `Ends ${dayjs(activeProduct.endedAt).format("MM/DD/YYYY")}`
                : "Cancel Plan"}
            </TextButton>
          </div>
        </div>
        <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-[12px]">
          {/* @ts-expect-error props */}
          <Typography variant="subtitle-m-bold">Billing & Payment History</Typography>
          <div className="tw-bg-white tw-p-[24px] tw-rounded-[8px] tw-border-solid tw-border-[1.5px] tw-border-gray-10 tw-flex tw-justify-between tw-items-center tw-gap-[16px]">
            {/* @ts-expect-error props */}
            <Typography variant="body-s" className="!tw-text-gray-50 tw-max-w-[260px]">
              Manage stored credit card information, download invoices and update your account details in your
              Billing Portal
            </Typography>
            {/* @ts-expect-error props */}
            <Button
              schema="secondary"
              className="tw-whitespace-nowrap"
              onClick={() => window.open(portalUrl, "_blank")}
            >
              Go to Billing Portal
            </Button>
          </div>
        </div>
      </div>
      <PhoneSystemCancelModal
        accountProduct={activeProduct}
        loading={loadingTrackingNumbers}
        onCancelPlan={handleCancelPlan}
        onClose={() => setIsCancelModalOpen(false)}
        open={isCancelModalOpen}
        trackingNumbers={trackingNumbers}
      />
      <Modal
        show={showChangePlanBar}
        onHide={() => setShowChangePlanBar(false)}
        dialogClassName="tw-flex tw-items-center tw-justify-center tw-h-full"
        contentClassName="tw-max-w-[900px] tw-max-h-[90vh] tw-w-full tw-flex tw-flex-col tw-gap-[32px]"
      >
        {/* @ts-expect-error props */}
        <Modal.Header title="Change Your Phone System Plan" closeButton />
        <Modal.Body className="tw-flex tw-flex-col tw-gap-[24px]">
          <ProductsStep1
            products={products}
            currentProductId={activeProduct?.product.details.productId}
            onSelectPlan={(product) => {
              setNewProduct(product);
              setShowChangePlanBar(false);
              void loadTrackingNumbers();
            }}
          />
        </Modal.Body>
      </Modal>

      <PhoneSystemChangeModal
        trackingNumbers={trackingNumbers}
        accountProduct={activeProduct}
        loadingTrackingNumbers={loadingTrackingNumbers}
        newProduct={newProduct ?? undefined}
        onClose={() => setNewProduct(null)}
        onChangePlan={handleChangeSubscription}
        open={Boolean(newProduct)}
      />
    </div>
  );
};

export default PhoneSystem;
